import { useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Box, Button, Flex } from '@gusto/workbench';
import {
  isLandingPage,
  isOrganicPage,
  isPartnersInvitePage,
  isReferrals,
} from 'helpers/visitor_page_helper';
import useReferralData from 'packages/signup/components/useReferralData';
import plantShopEmployeesSvg from 'signupV2/plant_shop_employees.svg';
import plantShopEmployeeCroppedSvg from 'signupV2/plant_shop_employee_cropped.svg';
import ProgressBar from 'components/progress_bar';
import { useSignupContext } from 'packages/signup/components/signupProvider';
import EOY22TermsDialog from 'packages/signup/components/EOY22TermsDialog';
import calculateProgressPercentage from 'packages/signup/components/layouts/progressPercentageCalculation';
import SinglePageFooterWrapper from 'packages/signup/components/layouts/singlePageComponents/SinglePageFooter';
import SinglePageWrapper from 'packages/signup/components/layouts/singlePageComponents/SinglePageWrapper';
import {
  SinglePageHeader,
  SinglePageSubHeader,
  SinglePageSubHeaderWrapper,
} from 'packages/signup/components/layouts/singlePageComponents/SinglePageHeading';

export default function SinglePageVariantOneLayout() {
  const signupContext = useSignupContext();

  const {
    referralSource,
    referrer,
    refereeAmount,
    doubleRefereeAmount,
    isEOY22ReferralCampaignActive,
  } = useReferralData();
  const { pathname } = useLocation();

  const [isTermsDialogOpen, setIsTermsDialogOpen] = useState(false);

  const referralHeaderCopy = `${referrer} invited you to try Gusto.`;

  const referralCopy = {
    referral: `Earn ${refereeAmount} after running your first payroll.`,
    referralEOY22: `Earn up to ${doubleRefereeAmount} after running your first payroll.`,
  };

  const RenderReferralContent = () => {
    return (
      <>
        <SinglePageHeader copy={referralHeaderCopy} />
        <SinglePageSubHeader
          copy={isEOY22ReferralCampaignActive ? referralCopy.referralEOY22 : referralCopy.referral}
          className="text-large-bold"
        />
        <SinglePageSubHeaderWrapper contentType="organic" />
      </>
    );
  };

  const RenderContent = () => {
    let content;
    if (referralSource) {
      content = <RenderReferralContent />;
    } else if (isLandingPage() || isPartnersInvitePage()) {
      content = <SinglePageWrapper contentType="non-organic" />;
    } else {
      content = <SinglePageWrapper contentType="organic" />;
    }
    return content;
  };

  return (
    <>
      <section className="single-page-variant-1">
        <Flex width="100wh" flexDirection="column" flexDirectionMd="row">
          {pathname !== '/sign_up_form' && !isReferrals() && !isOrganicPage() && (
            <ProgressBar percentage={calculateProgressPercentage(signupContext)} />
          )}
          <Flex
            heightMd="100vh"
            widthLg="50%"
            widthMd="54%"
            flexDirection="column"
            alignItems="center"
            justifyContent="space-between"
            className={`get-started-box ${isOrganicPage() || isReferrals() ? 'mobile-background' : ''}`}
          >
            {pathname === '/sign_up_form' ? (
              <Box textAlign="center" marginTop="20px">
                <h2>Thanks!</h2>
                <p className="layout-subheading">
                  Finish creating your account and don’t pay a cent until you run your first
                  payroll.
                </p>
              </Box>
            ) : (
              <Box
                textAlign="center"
                paddingTop="20px"
                paddingLeft="20px"
                paddingRight="20px"
                backgroundColor={isOrganicPage() || isReferrals() ? '' : 'salt.200'}
              >
                <RenderContent />
                {referralSource && isEOY22ReferralCampaignActive && (
                  <>
                    <Box component="p" className="text-default margin-top-25px">
                      <Button variant="link" onClick={() => setIsTermsDialogOpen(true)}>
                        Terms apply
                      </Button>
                    </Box>
                    <SinglePageFooterWrapper contentType="organic" />
                  </>
                )}
              </Box>
            )}
            <img
              className="plant-shop"
              alt="Happy employees in a plant shop"
              src={plantShopEmployeesSvg}
            />
            {pathname !== '/sign_up_form' && !isOrganicPage() && !isReferrals() && (
              <img
                className="plant-shop-cropped"
                alt="Happy employee in a plant shop"
                src={plantShopEmployeeCroppedSvg}
              />
            )}
          </Flex>
          <Flex
            widthLg="50%"
            widthMd="46%"
            alignItems="center"
            justifyContent="center"
            overflow="scroll"
            className="question-box"
          >
            <Outlet />
          </Flex>
        </Flex>
      </section>
      <EOY22TermsDialog open={isTermsDialogOpen} onClose={() => setIsTermsDialogOpen(false)} />
    </>
  );
}
